import Phaser from 'phaser';

/**
 * RoundedRect — Phaser Graphics-wrapper som tegner et rektangel med
 * runde hjørner. Brukes som drop-in-erstatter for `scene.add.rectangle`
 * der vi vil ha 2026-stil rounded corners uten å bytte til DOM-overlay.
 *
 * Designvalg:
 * - Origin er alltid (0, 0) for å matche typisk bruk i kodebasen
 *   (rectangle(x, y, w, h)).
 * - `radius` defaultes til 12px, som matcher moderne mobil-design.
 * - `stroke` er valgfri; settes via `setStrokeStyle(width, color, alpha)`.
 * - `setFillStyle(color, alpha)` reflekterer Phaser.Rectangle-API-et
 *   så call-sites kan bytte uten større endringer.
 * - `setSize(w, h)` re-tegner.
 *
 * Interaktivitet (`setInteractive`) settes på Graphics-objektet med
 * en `hitArea` av typen Phaser.Geom.Rectangle, ellers ville bare
 * pikslene i den tegnede formen vært klikkbare.
 */
export interface RoundedRectOptions {
  scene: Phaser.Scene;
  x: number;
  y: number;
  width: number;
  height: number;
  radius?: number;
  fillColor?: number;
  fillAlpha?: number;
  strokeColor?: number;
  strokeAlpha?: number;
  strokeWidth?: number;
}

export class RoundedRect {
  readonly gfx: Phaser.GameObjects.Graphics;
  private _width: number;
  private _height: number;
  private _radius: number;
  private _fillColor: number;
  private _fillAlpha: number;
  private _strokeColor: number;
  private _strokeAlpha: number;
  private _strokeWidth: number;
  private _x: number;
  private _y: number;

  constructor(opts: RoundedRectOptions) {
    this._x = opts.x;
    this._y = opts.y;
    this._width = opts.width;
    this._height = opts.height;
    this._radius = opts.radius ?? 12;
    this._fillColor = opts.fillColor ?? 0xffffff;
    this._fillAlpha = opts.fillAlpha ?? 1;
    this._strokeColor = opts.strokeColor ?? 0x000000;
    this._strokeAlpha = opts.strokeAlpha ?? 0;
    this._strokeWidth = opts.strokeWidth ?? 0;

    this.gfx = opts.scene.add.graphics();
    this.gfx.setPosition(opts.x, opts.y);
    this.redraw();
  }

  private redraw(): void {
    this.gfx.clear();
    if (this._fillAlpha > 0) {
      this.gfx.fillStyle(this._fillColor, this._fillAlpha);
      this.gfx.fillRoundedRect(0, 0, this._width, this._height, this._radius);
    }
    if (this._strokeAlpha > 0 && this._strokeWidth > 0) {
      this.gfx.lineStyle(this._strokeWidth, this._strokeColor, this._strokeAlpha);
      this.gfx.strokeRoundedRect(0, 0, this._width, this._height, this._radius);
    }
  }

  setFillStyle(color: number, alpha = 1): this {
    this._fillColor = color;
    this._fillAlpha = alpha;
    this.redraw();
    return this;
  }

  setStrokeStyle(width: number, color: number, alpha = 1): this {
    this._strokeWidth = width;
    this._strokeColor = color;
    this._strokeAlpha = alpha;
    this.redraw();
    return this;
  }

  setRadius(r: number): this {
    this._radius = r;
    this.redraw();
    return this;
  }

  setSize(width: number, height: number): this {
    this._width = width;
    this._height = height;
    this.redraw();
    return this;
  }

  setPosition(x: number, y: number): this {
    this._x = x;
    this._y = y;
    this.gfx.setPosition(x, y);
    return this;
  }

  setAlpha(alpha: number): this {
    this.gfx.setAlpha(alpha);
    return this;
  }

  setDepth(depth: number): this {
    this.gfx.setDepth(depth);
    return this;
  }

  setVisible(v: boolean): this {
    this.gfx.setVisible(v);
    return this;
  }

  /**
   * Gjør formen klikkbar. Phaser Graphics har ingen automatisk hitArea —
   * vi setter en eksplisitt Rectangle som matcher den tegnede formen.
   */
  setInteractive(opts: { useHandCursor?: boolean } = {}): this {
    const hit = new Phaser.Geom.Rectangle(0, 0, this._width, this._height);
    this.gfx.setInteractive({
      hitArea: hit,
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: !!opts.useHandCursor,
    });
    return this;
  }

  disableInteractive(): this {
    this.gfx.disableInteractive();
    return this;
  }

  on(event: string, handler: (...args: unknown[]) => void): this {
    this.gfx.on(event, handler);
    return this;
  }

  off(event: string, handler?: (...args: unknown[]) => void): this {
    this.gfx.off(event, handler);
    return this;
  }

  destroy(): void {
    this.gfx.destroy();
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get x(): number {
    return this._x;
  }

  get y(): number {
    return this._y;
  }
}

/**
 * Hjelper: tegner et rundet rektangel med skygge bak i ett kall.
 * Returnerer både skyggen og forgrunns-rektangelet.
 */
export function lagRoundedKortMedSkygge(
  scene: Phaser.Scene,
  opts: RoundedRectOptions & { skyggeOffset?: { x: number; y: number }; skyggeAlpha?: number },
): { skygge: RoundedRect; kort: RoundedRect } {
  const offset = opts.skyggeOffset ?? { x: 2, y: 4 };
  const skyggeAlpha = opts.skyggeAlpha ?? 0.22;
  const skygge = new RoundedRect({
    scene,
    x: opts.x + offset.x,
    y: opts.y + offset.y,
    width: opts.width,
    height: opts.height,
    radius: opts.radius ?? 12,
    fillColor: 0x000000,
    fillAlpha: skyggeAlpha,
  });
  const kort = new RoundedRect({ ...opts, scene });
  return { skygge, kort };
}
